import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import utils from "@utils";
import { TAX_EXEMPTION_TYPES, TAX_EXEMPTION_TYPES_MAP } from "@constants";
import { TaxExemptionService } from "@services";
import { HeaderGrid, ScrollDetect, Image } from "../../../components";

import "./TaxExemptionRequests.scss";

let cancel;

/** Gets a list of TaxExemptionRequests */
const TaxExemptionRequests = ({ router, user }) => {
	const {
		location: { query },
	} = router;
	const { lang } = global;
	const displayCount = 25;
	const [taxExemptionRequests, setTaxExemptionRequests] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [countsLoading, setCountsLoading] = useState(false);
	const [status, setStatus] = useState(query.status || TAX_EXEMPTION_TYPES.pending.type);

	const [counts, setCounts] = useState({
		Approved: 0,
		Denied: 0,
		Pending: 0,
	});

	const getTaxExemptionRequests = useCallback(() => {
		// If there is a request already running, abort it and start a new one
		if (cancel) {
			cancel();
		}

		const params = {
			queryParams: { pageNumber, displayCount, status },
			getCancelToken: c => {
				cancel = c;
			},
		};

		TaxExemptionService.getExemptionRequests(params)
			.then(({ data }) => {
				cancel = undefined;
				const itemList = data.ItemList ?? [];
				/** add disable button property */
				const newData = itemList.map(item => ({
					...item,
					approveButtonDisabled: false,
					denyButtonDisabled: false,
				}));

				setTaxExemptionRequests(
					pageNumber > 1 ? [...taxExemptionRequests, ...newData] : newData,
				);
				setPageNumber(pageNumber + 1);
				setHasMore(newData.length >= displayCount);
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					setTaxExemptionRequests([]);
					setPageNumber(1);
					setHasMore(false);
					const {
						response: { data },
					} = error;
					utils.notify({
						type: "error",
						message: data?.Message ?? lang.t("Error getting Tax Exemption Requests"),
						title: "Error",
					});
				}
			});
	});

	/**
	 * gets the counts for the header grid so we can let the user know how many requests have been approved, denied or are pending approval.
	 */
	const getCounts = () => {
		TaxExemptionService.getCounts()
			.then(({ data }) => {
				setCounts({ ...data });
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error getting Tax Exemption Counts"),
					title: "Error",
				});
			})
			.finally(() => setCountsLoading(false));
	};

	/**
	 * updates the buttonDisabled Status and sets the state with the remaining exemptions pending approval.
	 * @param {Number} id Id of the exemption that was recently approved or denied
	 * @param {Boolean} disabled Disabled value to edit the button state.
	 * @param {Boolean} isApprovedButtonPressed lets one know if the exemption was approved or not.
	 */
	const updateButtonDisabledStatus = (id, disabled, isApprovedButtonPressed) => {
		setTaxExemptionRequests(prevData => {
			const newData = [...prevData];
			const taxExemptionIndex = prevData.findIndex(
				({ TaxExemptionRequestId }) => TaxExemptionRequestId === id,
			);
			newData[taxExemptionIndex][
				isApprovedButtonPressed ? "approveButtonDisabled" : "denyButtonDisabled"
			] = disabled;

			return newData;
		});
	};

	/**
	 * updates taxExemptions whenever one is approved or denied
	 * @param {Number} id Is an Id that is provided to the network request
	 * @param {Boolean} approved is a boolean value thats lets one know if the user has accepted or denied the Tax Exemption.
	 */
	const updateTaxExemption = (id, approved) => () => {
		updateButtonDisabledStatus(id, true, approved);
		TaxExemptionService.validateExemption({ id, approved })
			.then(({ data: { Message } }) => {
				/** Retrieve latest counts and filter the newly modified request from the current list */
				getCounts();
				updateButtonDisabledStatus(id, false, approved);
				setTaxExemptionRequests(
					taxExemptionRequests.filter(
						({ TaxExemptionRequestId }) => TaxExemptionRequestId !== id,
					),
				);

				utils.notify({
					type: "success",
					title: lang.t("Success"),
					message: Message || lang.t("Tax exemption succesfully updated"),
				});
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error updating Tax Exemptions"),
					title: "Error",
				});
			});
	};

	/**
	 * navigates the user to the correct path depending on which type they clicked on in the header grid.
	 * @param {String} type could be either approved, denied or pending and helps one navigate to those lists.
	 */
	const onViewTypeChange = type => {
		setStatus(type);
		router.push({ pathname: "/admin/taxExemptionRequests/list", query: { status: type } });
	};

	/** Execute once component has been mounted */
	useEffect(() => {
		setCountsLoading(true);
		getCounts();
	}, []);

	/** Reset visibility of the scrolldetect element after it has become visible once */
	useEffect(() => {
		if (visible) {
			getTaxExemptionRequests();
			setVisible(false);
		}
	}, [visible]);

	/** Retrieve new TE requests whenever the status has been changed */
	useEffect(() => {
		setPageNumber(1);
		setTaxExemptionRequests([]);
		setHasMore(true);
	}, [status]);

	return (
		<div
			id="tax-exemption-requests"
			className="container"
		>
			<div className="row">
				<h2>{lang.t("Tax Exemption Requests")}</h2>
			</div>
			<div className="hg-container">
				<HeaderGrid
					gridOptions={TAX_EXEMPTION_TYPES_MAP}
					loadingCounts={countsLoading}
					counts={counts}
					activeGrid={status}
					setActive={onViewTypeChange}
				/>
			</div>
			<div className="row hidden-xs hiddem-sm table-headers">
				<div className="col-sm-3">
					<strong>Name</strong>
				</div>
				<div className="col-sm-2">
					<strong>Document</strong>
				</div>
				<div className="col-sm-2">
					<strong>Requested By</strong>
				</div>
				<div className="col-sm-2 text-right">
					<strong>Request Date</strong>
				</div>
			</div>
			<div className="table-body">
				<For
					each="request"
					index="index"
					of={taxExemptionRequests}
				>
					<div
						className="row v-align-sm"
						key={`exemption_request_${index}`}
					>
						<div className="col-xs-6 col-sm-3">
							<strong>{request.ClientName}</strong>
						</div>
						<div className="col-xs-6 text-right col-sm-2 view-doc-container">
							<a
								className="btn btn-default"
								href={request.TaxDocumentUrl}
								aria-label="Download Tax Exemption Document"
								rel="noopener noreferrer"
								target="_blank"
							>
								{lang.t("View")}
								<span className="visible-xs-inline">
									&nbsp;{lang.t("Document")}
								</span>
							</a>
						</div>
						<div className="col-sm-2 media">
							<div className="media-left">
								<Image
									className="img-circle media-object"
									src={request.RequestedByProfilePhotoURL}
									fallbackSrc="avatar"
								/>
							</div>
							<div className="media-body media-middle">
								<div className="media-heading">{request.RequestedByName}</div>
							</div>
						</div>
						<div className="col-sm-2 req-date">
							<span className="visible-xs-inline">{lang.t("Requested")}&nbsp;</span>
							{utils.formatDate(
								new Date(`${request.CreatedDateTime}Z`),
								"en-US",
								user.TimeZoneId,
							)}
						</div>
						<div className="col-sm-3 actions">
							<If condition={status !== "approved"}>
								<div
									className={`col-xs-6 ${status === "denied" ? "col-sm-offset-6" : ""}`}
								>
									<button
										id={`approve-${request.TaxExemptionRequestId}`}
										onClick={updateTaxExemption(
											request.TaxExemptionRequestId,
											true,
										)}
										type="button"
										disabled={request.approveButtonDisabled}
										className="btn btn-primary btn-block"
									>
										Approve
									</button>
								</div>
							</If>
							<If condition={status !== "denied"}>
								<div
									className={`col-xs-6 ${status === "approved" ? "col-sm-offset-6" : ""}`}
								>
									<button
										id={`deny-${request.TaxExemptionRequestId}`}
										onClick={updateTaxExemption(
											request.TaxExemptionRequestId,
											false,
										)}
										type="button"
										disabled={request.denyButtonDisabled}
										className="btn btn-danger btn-block"
									>
										Deny
									</button>
								</div>
							</If>
						</div>
					</div>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>
		</div>
	);
};

TaxExemptionRequests.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
};

TaxExemptionRequests.defaultProps = {
	user: {},
};

export default TaxExemptionRequests;
